import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useUserLimits } from "./useUserLimits";
import moment from "moment";

export function useCreateUser() {
  const store = useStore();

  // Get shared user limits
  const {
    isAgentLimit,
    isReporterLimit,
    isAdminLimit,
    noAgent,
    noReporter,
    noAdmin,
    inLimits,
    initUserLimits,
  } = useUserLimits();

  // Form state
  const valid = ref(true);
  const setSkillMandatory = ref(true);
  const uname = ref("");
  const validityRange = ref([moment().format("YYYY-MM-DD"), ""]);
  const fullname = ref("");
  const email = ref("");
  const phone = ref("");
  const password = ref("");
  const confPassword = ref("");
  const userType = ref(1);
  const skills = ref([]);
  const showPass = ref(false);
  const showConf = ref(false);
  const setExpiration = ref(false);

  // Local state
  const startDate = ref(moment().format("YYYY-MM-DD"));
  const endDate = ref("");
  const userPermission = ref([]);

  // Computed properties
  const domainSkills = computed(() => store.getters.domainSkills);
  const domainRanges = computed(() => store.getters.domainRanges);
  const domainUsers = computed(() => store.getters.domainUsers);
  const domain = computed(() => store.getters.domain);
  const permissions = computed(() => store.getters.permissions);
  const editAdminPermission = computed(() => store.getters.editAdminPermission);
  const getbaseURL = computed(() => store.getters.getbaseURL);

  // Validation rules
  const unameRules = [
    (v) => !!v || "Username is required",
    (v) => (!!v && v.length >= 3) || "Username must be at least 3 characters",
    (v) => (!!v && v.length <= 5) || "Username must be at most 5 characters",
    (v) => (!!v && !isNaN(v)) || "Username must be a number",
  ];

  const passwordRules = [
    (v) => !!v || "Password is required",
    (v) =>
      /^[a-zA-Z0-9_]*$/.test(v) ||
      "Password can only contain letters, numbers, and underscores",
    (v) => (v && v.length >= 10) || "Password must be at least 10 characters",
  ];

  const nameRules = [
    (v) => !!v || "Full name is required",
    (v) =>
      (!!v && v.trim().length >= 4) ||
      "Full name must be at least 4 characters",
  ];

  const numberRules = [
    (v) => {
      // Phone is optional, only validate if a value is provided
      if (!v || v === "") return true;
      if (!isNaN(v) && (v.startsWith("05") || v.startsWith("01") || v.startsWith("966"))) {
        return true;
      }
      return "Invalid phone number format";
    },
    (v) => {
      // Phone is optional, only validate if a value is provided
      if (!v || v === "") return true;
      if (v.length >= 10) return true;
      return "Phone number must be at least 10 digits";
    },
    (v) => {
      // Phone is optional, only validate if a value is provided
      if (!v || v === "") return true;
      if (v.length <= 12) return true;
      return "Phone number must be at most 12 digits";
    },
  ];

  const emailRules = [
    (v) => !!v || "Email is required",
    (v) =>
      (v &&
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(
          v
        )) ||
      "Invalid email format",
  ];

  const confirmPasswordRules = [
    (v) => !!v || "Password confirmation is required",
  ];

  const startDateRules = [
    (v) =>
      (!!v && moment(v, "YYYY-MM-DD", true).isValid()) || "Invalid date format",
    (v) => !!v || "Start date is required",
  ];

  const endDateRules = [
    (v) =>
      (!!v && moment(v, "YYYY-MM-DD", true).isValid()) || "Invalid date format",
  ];

  // Computed validations
  const passwordConfirmationRule = computed(() => {
    return () =>
      password.value === confPassword.value || "Passwords do not match";
  });

  const confirmUnameRules = computed(() => {
    return () =>
      !domainUsers.value.includes(uname.value) || "Username already exists";
  });

  const confirmUnameRange = computed(() => {
    return () => existInRanges.value || "Username is not in allowed range";
  });

  const existInRanges = computed(() => {
    for (const userRange of domainRanges.value) {
      if (userRange.start <= uname.value && userRange.end >= uname.value) {
        return true;
      }
    }
    return false;
  });

  const getRangeHints = computed(() => {
    let hint = "";
    if (!domainRanges.value) {
      return "";
    }
    for (let i = 0; i < domainRanges.value.length; i++) {
      const range = domainRanges.value[i];
      hint += range.start + "--" + range.end;
      if (i !== domainRanges.value.length - 1) {
        hint += ", ";
      }
    }
    return hint;
  });

  const enableAdminType = computed(() => {
    return hasPermission(editAdminPermission.value);
  });

  const enableCreateBtn = computed(() => {
    if (!uname.value || uname.value === "") return false;
    if (uname.value.trim().length < 3 || uname.value.trim().length > 5)
      return false;
    if (!existInRanges.value) return false;
    if (fullname.value === "" || !fullname.value) return false;
    if (fullname.value.trim().length < 4) return false;

    // Phone is optional - only validate if a value is provided
    if (phone.value != null && phone.value !== "") {
      // Check length
      if (phone.value.length < 10 || phone.value.length > 12) {
        return false;
      }
      // Check format - must start with 05, 01, or 966
      if (
        !phone.value.startsWith("05") &&
        !phone.value.startsWith("01") &&
        !phone.value.startsWith("966")
      ) {
        return false;
      }
      // Check if it's a number
      if (isNaN(phone.value)) {
        return false;
      }
    }

    if (
      !email.value ||
      email.value === "" ||
      !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(
        email.value
      )
    ) {
      return false;
    }

    if (password.value == null || password.value === "") return false;
    if (!/^[a-zA-Z0-9_]*$/.test(password.value)) return false;
    if (confPassword.value !== password.value) return false;
    if (!inLimits.value) return false;

    if (isAgentLimit.value && userType.value === 1) return false;
    if (isReporterLimit.value && userType.value === 2) return false;
    if (isAdminLimit.value && userType.value === 3) return false;

    return true;
  });

  const skilsNames = computed(() => {
    return domainSkills.value || [];
  });

  // Methods
  const hasPermission = (permission) => {
    return userPermission.value.includes(permission);
  };

  const clear = () => {
    uname.value = "";
    validityRange.value = [moment().format("YYYY-MM-DD"), ""];
    fullname.value = "";
    email.value = "";
    phone.value = "";
    password.value = "";
    confPassword.value = "";
    userType.value = 1;
    skills.value = [];
    setExpiration.value = false;
    if (domainSkills.value.length > 0) {
      skills.value.push(domainSkills.value[0].name);
    }
  };

  const reset = () => {
    // Clear the form
    clear();
    // Also clear the store's userInfo to prevent form population
    store.commit("setUserInfo", { userInfo: {} });
  };

  const createUser = async () => {
    startDate.value = moment(startDate.value).format("YYYY-MM-DD");
    if (setExpiration.value) {
      endDate.value = moment(endDate.value).format("YYYY-MM-DD");
    } else {
      endDate.value = "-";
    }

    store.dispatch("ShowProgressBar", {
      show: true,
      message: "Creating user....",
    });

    try {
      const resp = await store.dispatch("CreateNewUser", {
        username: uname.value,
        fullname: fullname.value,
        phone: phone.value,
        email: email.value,
        type: userType.value,
        password: password.value,
        expireable: setExpiration.value,
        expiredate: endDate.value,
        startdate: startDate.value,
        skills: skills.value,
      });

      store.dispatch("ShowProgressBar", { show: false, message: "" });

      if (!resp.success) {
        store.dispatch("SetMessageDialog", {
          show: true,
          message: resp.message,
        });
      } else {
        store.dispatch("SetMessageDialog", {
          show: true,
          message: resp.message,
          success: resp.success,
        });
        clear();
        await initUserLimits();
      }

      return resp.success;
    } catch (err) {
      console.log("Create user error: ", err);
      let message = err.message;
      if (err.response) {
        message = err.response.data.message;
      }
      store.dispatch("ShowProgressBar", { show: false, message: "" });
      store.dispatch("SetMessageDialog", { show: true, message: message });
      return false;
    }
  };

  // Watchers
  watch(userType, (newValue) => {
    if (newValue === 1) {
      setSkillMandatory.value = true;
      if (skills.value.length === 0 && domainSkills.value.length > 0) {
        skills.value.push(domainSkills.value[0].name);
      }
    } else {
      setSkillMandatory.value = false;
      skills.value = [];
    }
  });

  watch(validityRange, (val) => {
    if (val.length === 2) {
      startDate.value = moment(val[0]).format("YYYY-MM-DD");
      endDate.value =
        isNaN(val[1]) === false ? moment(val[1]).format("YYYY-MM-DD") : "";
    }
  });

  watch(domain, async (newValue) => {
    if (newValue) {
      userPermission.value = permissions.value;
    }
  });

  watch(setExpiration, (isSet) => {
    if (!isSet) {
      validityRange.value = [moment().format("YYYY-MM-DD"), ""];
    }
  });

  watch(skills, (newVal, oldVal) => {
    if (newVal.length === 0 && oldVal.length > 0 && userType.value === 1) {
      skills.value = [oldVal[0]];
    }
  });

  // Lifecycle
  onMounted(async () => {
    if (domain.value) {
      userPermission.value = permissions.value;
    }
  });

  return {
    // Form state
    valid,
    setSkillMandatory,
    uname,
    validityRange,
    fullname,
    email,
    phone,
    password,
    confPassword,
    userType,
    skills,
    showPass,
    showConf,
    setExpiration,

    // Local state
    startDate,
    endDate,
    userPermission,

    // Computed
    domainSkills,
    domainRanges,
    domainUsers,
    domain,
    permissions,
    editAdminPermission,
    getbaseURL,
    passwordConfirmationRule,
    confirmUnameRules,
    confirmUnameRange,
    existInRanges,
    getRangeHints,
    enableAdminType,
    enableCreateBtn,
    skilsNames,

    // Shared limits (from useUserLimits)
    isAgentLimit,
    isReporterLimit,
    isAdminLimit,
    noAgent,
    noReporter,
    noAdmin,
    inLimits,

    // Validation rules
    unameRules,
    passwordRules,
    nameRules,
    numberRules,
    emailRules,
    confirmPasswordRules,
    startDateRules,
    endDateRules,

    // Methods
    hasPermission,
    clear,
    reset,
    createUser,
  };
}
